import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

/**
 * Public /forgot-password page — user enters their email and receives a
 * secure reset link. Backend always responds 200 to avoid leaking which
 * emails exist, so the UI simply confirms delivery.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const r = await fetch(`${API}/auth/password-reset/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            if (!r.ok) throw new Error("Something went wrong. Try again in a moment.");
            setSent(true);
        } catch (e) {
            toast.error(e.message);
        } finally { setSubmitting(false); }
    }

    return (
        <div className="min-h-screen bg-[#0B0D11] flex items-center justify-center p-6" data-testid="forgot-password-page">
            <div className="w-full max-w-md bg-white rounded-md shadow-xl overflow-hidden">
                <div className="bg-[#0B0D11] px-8 py-6">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0A0A0A]">RPPL Solar</div>
                    <div className="text-lg font-semibold text-white mt-1">Reset your password</div>
                </div>
                <div className="p-8">
                    {sent ? (
                        <div className="space-y-4" data-testid="forgot-password-sent">
                            <div className="flex items-start gap-3 p-4 rounded-sm bg-green-50 border border-green-200 text-green-800 text-sm">
                                <MailCheck className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-semibold">Check your inbox</div>
                                    <div className="text-xs mt-0.5">
                                        If <span className="font-mono">{email}</span> is a registered account, we've sent a secure link to set a new password. The link expires in 24 hours.
                                    </div>
                                </div>
                            </div>
                            <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#0A0A0A]" data-testid="back-to-login">
                                <ArrowLeft className="w-3 h-3" /> Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <p className="text-sm text-zinc-500">
                                Enter your account email and we'll send you a secure link to set a new password.
                            </p>
                            <div>
                                <label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Email</label>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    autoFocus
                                    className="rounded-sm"
                                    data-testid="forgot-email"
                                />
                            </div>
                            <Button type="submit" disabled={submitting || !email} className="w-full bg-[#0A0A0A] hover:bg-black text-white rounded-full h-10" data-testid="forgot-submit">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                            </Button>
                            <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#0A0A0A]" data-testid="back-to-login">
                                <ArrowLeft className="w-3 h-3" /> Back to sign in
                            </Link>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
